import React from "react";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Divider from "@material-ui/core/Divider";
import { makeStyles } from "@material-ui/core/styles";
import colors from "../../assets/v3/base/colors";
import InfoRounded from '@material-ui/icons/InfoRounded';
import CallRounded from '@material-ui/icons/CallRounded';
import MonetizationOnRounded from '@material-ui/icons/MonetizationOnRounded';
import SecurityRounded from '@material-ui/icons/SecurityRounded';
import DescriptionRounded from '@material-ui/icons/DescriptionRounded';
import LogoURLMap, { hostnamesMap } from "../../components/LogoURLMap";

const useStyles = makeStyles((theme) => ({
  footer: {
    backgroundColor: colors.dark.main,
    color: colors.white.main,
    marginTop: "auto",
    width: "100%",
    flexShrink: 0,
    padding: 0,
  },
  footerContent: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: theme.spacing(4, 3),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(3, 2),
    },
  },
  footerSection: {
    padding: theme.spacing(0, 2),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2, 0),
      textAlign: "center",
    },
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: "1rem",
    marginBottom: theme.spacing(2),
    color: colors.white.main,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  link: {
    display: "flex",
    alignItems: "center",
    color: colors.grey[300],
    textDecoration: "none",
    marginBottom: theme.spacing(1.5),
    fontSize: "0.875rem",
    transition: "all 0.3s ease",
    "&:hover": {
      color: colors.white.main,
      transform: "translateX(4px)",
    },
    [theme.breakpoints.down("sm")]: {
      justifyContent: "center",
      "&:hover": {
        transform: "none",
      },
    },
  },
  icon: {
    marginRight: theme.spacing(1),
    fontSize: "1.2rem",
  },
  divider: {
    backgroundColor: colors.grey[700],
    margin: theme.spacing(2, 0),
  },
  bottomBar: {
    backgroundColor: colors.grey[900],
    padding: theme.spacing(2, 3),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2, 2),
      textAlign: "center",
    },
  },
  bottomContent: {
    maxWidth: "1200px",
    margin: "0 auto",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      gap: theme.spacing(1),
    },
  },
  copyright: {
    color: colors.grey[400],
    fontSize: "0.875rem",
  },
  logoContainer: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      justifyContent: "center",
    },
  },
  logo: {
    maxHeight: "40px",
    maxWidth: "150px",
  },
  description: {
    color: colors.grey[400],
    fontSize: "0.875rem",
    lineHeight: 1.6,
    marginTop: theme.spacing(1),
  },
}));

const RegFooter = () => {
  const classes = useStyles();
  const origin = window.location.origin;
  const instituteId =
    hostnamesMap[window.location.hostname] || "5d679d49c136660a09596d85";
  const stuff = LogoURLMap[instituteId] || {};
  const logoUrl = stuff.url || stuff.logo;

  return (
    <Box className={classes.footer}>
      <Box className={classes.footerContent}>
        <Grid container spacing={4}>
          {/* Company/Logo Section */}
          <Grid item xs={12} sm={6} md={4}>
            <Box className={classes.footerSection}>
              {logoUrl && (
                <Box className={classes.logoContainer}>
                  <img
                    src={logoUrl}
                    alt="Company Logo"
                    className={classes.logo}
                  />
                </Box>
              )}
              <Typography className={classes.description}>
                Empowering students with quality education and comprehensive
                learning solutions.
              </Typography>
            </Box>
          </Grid>

          {/* Quick Links Section */}
          <Grid item xs={12} sm={6} md={4}>
            <Box className={classes.footerSection}>
              <Typography className={classes.sectionTitle}>Quick Links</Typography>
              <Box component="a" href={`${origin}/quick-links?p=about_us`} target="_blank" className={classes.link}>
                <InfoRounded className={classes.icon} />
                About Us
              </Box>
              <Box component="a" href={`${origin}/quick-links?p=contact_address`} target="_blank" className={classes.link}>
                <CallRounded className={classes.icon} />
                Contact Us
              </Box>
              <Box component="a" href={`${origin}/quick-links?p=cancellation_policy`} target="_blank" className={classes.link}>
                <MonetizationOnRounded className={classes.icon} />
                Refund/Cancellation Policy
              </Box>
            </Box>
          </Grid>

          {/* Legal Section */}
          <Grid item xs={12} sm={12} md={4}>
            <Box className={classes.footerSection}>
              <Typography className={classes.sectionTitle}>Legal</Typography>
              <Box component="a" href={`${origin}/quick-links?p=privacy_policy`} target="_blank" className={classes.link}>
                <SecurityRounded className={classes.icon} />
                Privacy Policy
              </Box>
              <Box component="a" href={`${origin}/quick-links?p=terms_and_conditions`} target="_blank" className={classes.link}>
                <DescriptionRounded className={classes.icon} />
                Terms & Conditions
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Divider className={classes.divider} />

      {/* Bottom Bar */}
      <Box className={classes.bottomBar}>
        <Box className={classes.bottomContent}>
          <Typography className={classes.copyright}>
            © {new Date().getFullYear()} All rights reserved.
          </Typography>
          <Typography className={classes.copyright}>
            Designed with ❤️ for better education
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default RegFooter;
